<template>
  <div class="document-preview">
    <div v-if="isLoading" class="preview-state">Hämtar dokument...</div>
    <div v-else-if="error" class="preview-state preview-error">{{ error }}</div>
    <div v-else-if="!src" class="preview-state">Inget dokument valt.</div>

    <template v-else>
      <div v-if="showControls" class="controls-parent">
        <div class="controls" aria-label="Dokumentverktyg">
          <AppButton
            v-if="documentKind === 'image'"
            class="control-button"
            icon
            color="transparent"
            aria-label="Zooma in"
            title="Zooma in"
            @click="zoomIn"
          >
            <SvgIcon :path="mdiMagnifyPlusOutline" :size="22" />
          </AppButton>
          <AppButton
            v-if="documentKind === 'image'"
            class="control-button"
            icon
            color="transparent"
            aria-label="Zooma ut"
            title="Zooma ut"
            @click="zoomOut"
          >
            <SvgIcon :path="mdiMagnifyMinusOutline" :size="22" />
          </AppButton>
          <AppButton
            v-if="documentKind === 'image'"
            class="control-button"
            icon
            color="transparent"
            aria-label="Rotera 90 grader"
            title="Rotera 90 grader"
            @click="rotate"
          >
            <SvgIcon :path="mdiRotateRight" :size="22" />
          </AppButton>
          <AppButton
            v-if="documentKind === 'image'"
            class="control-button"
            icon
            color="transparent"
            aria-label="Återställ bilden"
            title="Återställ bilden"
            @click="reset"
          >
            <SvgIcon :path="mdiRestore" :size="22" />
          </AppButton>
          <AppButton
            class="control-button"
            icon
            color="transparent"
            aria-label="Ladda hem"
            title="Ladda hem"
            @click="downloadDocument"
          >
            <SvgIcon :path="mdiDownload" :size="22" />
          </AppButton>
        </div>
      </div>

      <iframe
        v-if="documentKind === 'pdf'"
        :src="src"
        :title="fileName || 'Dokument'"
        type="application/pdf"
        class="fluid pdf-preview"
      />

      <ImagePreview v-else-if="documentKind === 'image'" ref="imagePreviewComponent" :src="src" :alt="fileName || ''" />

      <div v-else class="preview-state">
        Filtypen kan inte visas.
        <a :href="src" target="_blank" rel="noopener noreferrer">Öppna i ny flik</a>
      </div>

      <div v-if="$slots.default" class="container-root">
        <div class="slot-container">
          <div class="slot">
            <slot />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { mdiDownload, mdiMagnifyMinusOutline, mdiMagnifyPlusOutline, mdiRestore, mdiRotateRight } from '@mdi/js'

import AppButton from '../AppButton/AppButton.vue'
import SvgIcon from '../SvgIcon/SvgIcon.vue'
import ImagePreview from './ImagePreview.vue'
import type { ImagePreviewControls } from './ImagePreview.vue'

type DocumentKind = 'pdf' | 'image' | 'unsupported'

const fileExtension = (fileName?: string) => {
  return fileName?.split('.').pop()?.toLowerCase() ?? ''
}

const props = withDefaults(
  defineProps<{
    src?: string
    contentType?: string
    fileName?: string
    fileType?: string
    isLoading?: boolean
    error?: string
    hideControls?: boolean
  }>(),
  {
    src: '',
    contentType: '',
    fileName: '',
    fileType: '',
    isLoading: false,
    error: '',
    hideControls: false,
  },
)

const imagePreviewComponent = ref<ImagePreviewControls>()

const documentKind = computed<DocumentKind>(() => {
  const contentType = props.contentType.toLowerCase()
  const extension = (props.fileType || fileExtension(props.fileName)).toLowerCase()

  if (contentType.split(';')[0].trim() === 'application/pdf' || extension === 'pdf') {
    return 'pdf'
  }

  if (contentType.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(extension)) {
    return 'image'
  }

  return 'unsupported'
})

const showControls = computed(() => !props.hideControls && ['image', 'pdf'].includes(documentKind.value))
const downloadFileName = computed(() => props.fileName || 'dokument')

const zoomIn = () => imagePreviewComponent.value?.zoomIn()
const zoomOut = () => imagePreviewComponent.value?.zoomOut()
const rotate = () => imagePreviewComponent.value?.rotate()
const reset = () => imagePreviewComponent.value?.reset()

const downloadDocument = () => {
  const link = document.createElement('a')
  link.href = props.src
  link.download = downloadFileName.value
  link.click()
}
</script>

<style lang="scss" scoped>
.document-preview {
  position: relative;
  flex: 1 1 auto;
  height: 100%;
  width: 100%;
  min-height: 320px;
  background-color: #525659;
  color: #f8fafc;
  overflow: hidden;
}

.fluid {
  height: 100%;
  width: 100%;
}

.pdf-preview {
  border: 0;
  border-radius: 4px;
}

.preview-state {
  height: 100%;
  min-height: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px;
  text-align: center;
  color: #f8fafc;

  a {
    color: #bfdbfe;
  }
}

.preview-error {
  color: #fecaca;
}

.controls-parent {
  position: absolute;
  top: 15px;
  left: 15px;
  bottom: 15px;
  display: flex;
  align-items: center;
  pointer-events: none;
  z-index: 20;
}

.controls {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px;
  pointer-events: all;
  border-radius: 6px;
  border: 1px solid rgba(15, 23, 42, 0.14);
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.18);

  .control-button {
    width: 32px;
    height: 32px;
    border-radius: 4px;
    color: #0f172a;

    &:hover {
      background: #e2e8f0;
    }

    :deep(.btn-text) {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
}

.container-root {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-end;
  max-width: 420px;
  min-width: 420px;
  max-height: 100%;
  background-color: #fff;
  color: #0f172a;
}

.slot-container {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
}

.slot {
  overflow-y: auto;
  margin-bottom: 70px;
  height: 100%;
}
</style>
